import { Address, beginCell, Cell, Contract, Dictionary, contractAddress, ContractProvider, Sender, SendMode, toNano, ContractABI, StateInit } from '@ton/core';
import { Address as TonAddress} from 'ton'
import { SendTransactionRequest, TonConnectUI } from '@tonconnect/ui-react';
import BN from "bn.js";
import { getClient } from '../lib/get-ton-client';
import { waitForSeqno } from '../lib/utils';
import { cellToAddress, makeGetCall } from 'lib/make-get-call';
import { PRESALE_ADDRESS } from 'constant';
import { readJettonMetadata } from 'lib/jetton-minter';
import { Maybe } from '@ton/core/dist/utils/maybe';
import { WalletContract } from 'tonweb/dist/types/contract/wallet/wallet-contract';
import { WalletContractV4 } from '@ton/ton';

export type PresaleContent = {
    type: 0 | 1,
    uri: string
};

export type PresaleConfig = {totalSupply: bigint, admin: Address; wallet_code: Cell, state: number, price: bigint, cap: bigint, presale_start_date: number, presale_end_date: number };
export type PresaleWalletConfig = {ownerAddress: Address, presaleAddress: Address}

export function jettonMinterConfigToCell(config: PresaleConfig): Cell {
    return beginCell()
        .storeUint(config.totalSupply, 64)
        .storeBit(config.state)
        .storeUint(config.price, 64)
        .storeUint(config.cap, 64)
        .storeUint(config.presale_start_date, 32)
        .storeUint(config.presale_end_date, 32)
        .storeDict(Dictionary.empty(
            Dictionary.Keys.Address(),
            Dictionary.Values.Cell(),
          ))
        .storeAddress(config.admin)
        .storeRef(config.wallet_code)
        .endCell();
}

export function presaleWalletMinterConfigToCell(config: PresaleWalletConfig): Cell {
    return beginCell()
        .storeAddress(config.ownerAddress)
        .storeAddress(config.presaleAddress)
        .storeCoins(0)
        .storeCoins(0)
        .storeCoins(0)
        .endCell()
}

export  const getPresaleWalletData =  async (presaleWalletAddress: any) => {
    const tc = await getClient();
    const contractAddr = TonAddress.parse(presaleWalletAddress)
    const minter = await makeGetCall(
        contractAddr,
        "get_jetton_data",
        [],
        async ([jetton_amount, staked_amount, is_claimed, user_address, presale_address]) => ({
            jetton_amount: Number(jetton_amount) / 1000000000,
            staked_amount: Number(staked_amount) / 1000000000,
            is_claimed,
            user_address: user_address?.toString(),
            presale_address: presale_address?.toString()
        }),
        tc,
      );
      
      return minter;
    }

export class Presale implements Contract {
    constructor(readonly address: Address, readonly init?: { code: Cell; data: Cell }) { }

    static createFromAddress(address: Address) {
        return new Presale(address);
    }

    static createFromConfig(config: PresaleConfig, code: Cell, workchain = 0) {
        const data = jettonMinterConfigToCell(config);
        const init = { code, data };
        return new Presale(contractAddress(workchain, init), init);
    }

    static createWalletAddressFromConfig(config: PresaleWalletConfig, code: Cell, workchain = 0) {
        const data = presaleWalletMinterConfigToCell(config);
        const init = {code, data};
        return contractAddress(workchain, init)
    }


    async sendDeploy(provider: ContractProvider, via: Sender, value: bigint) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY
        });
    }

    // static mintMessage(to: Address, jetton_amount: bigint, forward_ton_amount: bigint, total_ton_amount: bigint,) {
    //     return beginCell().storeUint(0x4fda1e51, 32).storeUint(0, 64) // op, queryId
    //         .storeAddress(to).storeCoins(jetton_amount)
    //         .storeCoins(forward_ton_amount).storeCoins(total_ton_amount)
    //         .endCell();
    // }

    static mintMessage(
        to: Address,
        jettonValue: bigint,
        transferToJWallet: bigint,
        queryId: number,
      ): Cell {
        return beginCell()
          .storeUint(21, 32)
          .storeUint(queryId, 64) // queryid
          .storeAddress(to)
          .storeCoins(transferToJWallet)
          .storeRef(
            // internal transfer message
            beginCell()
              .storeUint(0x178d4519, 32)
              .storeUint(0, 64)
              .storeCoins(jettonValue)
              .storeAddress(null)
              .storeAddress(to)
              .storeCoins(toNano(0.001))
              .storeBit(false) // forward_payload in this slice, not separate cell
              .endCell(),
          )
          .endCell();
      }

    // async sendMint(provider: ContractProvider, via: Sender, to: Address, jetton_amount: bigint, forward_ton_amount: bigint, total_ton_amount: bigint,) {
    //     await provider.internal(via, {
    //         sendMode: SendMode.PAY_GAS_SEPARATELY,
    //         body: Presale.mintMessage(to, jetton_amount, forward_ton_amount, total_ton_amount,),
    //         value: total_ton_amount + toNano("0.1"),
    //     });
    // }

    async sendMint(provider: ContractProvider, via: Sender, to: Address, jettonValue: bigint) {
        await provider.internal(via, {
                    sendMode: SendMode.PAY_GAS_SEPARATELY,
                    body: Presale.mintMessage(to, jettonValue, toNano('0.05'), 0),
                    value: toNano("0.02"),
                });
    }

    static discoveryMessage(owner: Address, include_address: boolean) {
        return beginCell().storeUint(0x2c76b973, 32).storeUint(0, 64) // op, queryId
            .storeAddress(owner).storeBit(include_address)
            .endCell();
    }

    async sendDiscovery(provider: ContractProvider, via: Sender, owner: Address, include_address: boolean, value: bigint = toNano('0.1')) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Presale.discoveryMessage(owner, include_address),
            value: value,
        });
    }

    static changeAdminMessage(newOwner: Address) {
        return beginCell().storeUint(0x4840664f, 32).storeUint(0, 64) // op, queryId
            .storeAddress(newOwner)
            .endCell();
    }

    async sendChangeAdmin(provider: ContractProvider, via: Sender, newOwner: Address) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Presale.changeAdminMessage(newOwner),
            value: toNano("0.1"),
        });
    }


    static stateMessage(state: boolean) {
        return beginCell().storeUint(0x58ca5361, 32).storeUint(0, 64) // op, queryId
            .storeBit(state)
            .endCell();
    }

    async sendChangeState(provider: ContractProvider, via: Sender, state: boolean) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Presale.stateMessage(state),
            value: toNano('0.2')
        });

    }

    static withdrawMessage() {
        return beginCell().storeUint(0x46ed2e94, 32).storeUint(0, 64) // op, queryId
            .endCell();
    }

    async sendWithdraw(provider: ContractProvider, via: Sender) {
        await provider.internal(via, {
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Presale.withdrawMessage(),
            value: toNano('0.1')
        });

    }

    static buyMessage() {
        return beginCell().storeUint(0x402eff0b, 32).storeUint(0, 64) // op, queryId
            .endCell();
    }

    // async sendBuy(provider: ContractProvider, via: Sender, value: bigint) {
    //     await provider.internal(via, {
    //         sendMode: SendMode.PAY_GAS_SEPARATELY,
    //         body: Presale.buyMessage(),
    //         value
    //     });

    // }

    async sendBuy(
        tonConnection: TonConnectUI,
        presaleContract: Address,
        amount: bigint,
        walletAddress: string,
      ) {
        const tc = await getClient();
        const waiter = await waitForSeqno(
          tc.openWalletFromAddress({
            source: TonAddress.parse(walletAddress),
          }),
        );
    
        const tx: SendTransactionRequest = {
          validUntil: Date.now() + 5 * 60 * 1000,
          messages: [
            {
              address: presaleContract.toString(),
              amount: amount.toString(),
              stateInit: undefined,
              payload: Presale.buyMessage()
                .toBoc()
                .toString("base64"),
            },
          ],
        };
    
        await tonConnection.sendTransaction(tx);
        await waiter();
      }


    static claimMessage() {
        return beginCell().storeUint(0x43c7d5c9, 32).storeUint(0, 64).storeAddress(Address.parse("kQAyfSF2JRHQ60AlUlvwsWYytIfGsM3H_hljkVEtkSRU6Qr6")) // op, queryId
        .endCell();
    }
    async sendClaim(
        tonConnection: TonConnectUI,
        presaleContract: Address,
        walletAddress: string,
      ) {
        const tc = await getClient();
        const waiter = await waitForSeqno(
          tc.openWalletFromAddress({
            source: TonAddress.parse(walletAddress),
          }),
        );
        
        const tx: SendTransactionRequest = {
          validUntil: Date.now() + 5 * 60 * 1000,
          messages: [
            {
              address: presaleContract.toString(),
              amount: toNano(0.05).toString(),
              stateInit: undefined,
              payload: Presale.claimMessage()
                .toBoc()
                .toString("base64"),
            },
          ],
        };
    
        await tonConnection.sendTransaction(tx);
        await waiter();
    }


    async getJettonData(provider: ContractProvider) {
        let res = await provider.get('get_jetton_data', []);
        let totalSupply = res.stack.readBigNumber();
        let mintable = res.stack.readBoolean();
        let adminAddress = res.stack.readAddress();
        let walletCode = res.stack.readCell();
        let user_data = res.stack.readCell();
        // console.log(user_data)
        return {
            totalSupply,
            mintable,
            adminAddress,
            walletCode,
            user_data
        };
    }

    async getTotalSupply(provider: ContractProvider) {
        let res = await this.getJettonData(provider);
        return res.totalSupply;
    }

    async getAdminAddress(provider: ContractProvider) {
        let res = await this.getJettonData(provider);
        
        return res.adminAddress;
    }


    async getPRESALEData() {
        const tc = await getClient();
        const contractAddr = TonAddress.parse(PRESALE_ADDRESS)
        const minter = await makeGetCall(
            contractAddr,
            "get_presale_data",
            [],
            async ([total_supply, state, price, cap, start_date, end_date]) => ({
                total_supply: Number(total_supply) / 1000000000,
                state: Number(state),
                price: Number(price) / 1000000000,
                cap: Number(cap) / 1000000000,
                start_date: start_date?.toString(),
                end_date: end_date?.toString()
            }),
            tc,
          );
          return minter;
        // let res = await provider.get('get_presale_data', []);
        // let state = res.stack.readBoolean();
        // let price = res.stack.readBigNumber();
        // let cap = res.stack.readBigNumber();
        // let start_date = res.stack.readNumber();
        // let end_date = res.stack.readNumber();
        // return {
        //     state,
        //     price,
        //     cap,
        //     start_date,
        //     end_date
        // };
    }

    // async getPRESALEState(provider: ContractProvider) {
    //     let res = await this.getPRESALEData(provider);
    //     return res.state;
    // }

    // async getPRESALEPrice(provider: ContractProvider) {
    //     let res = await this.getPRESALEData(provider);
    //     return res.price;
    // }

    // async getPRESALECap(provider: ContractProvider) {
    //     let res = await this.getPRESALEData(provider);
    //     return res.cap;
    // }

    // async getPRESALEStartDate(provider: ContractProvider) {
    //     let res = await this.getPRESALEData(provider);
    //     return res.start_date;
    // }

    // async getPRESALEEndDate(provider: ContractProvider) {
    //     let res = await this.getPRESALEData(provider);
    //     return res.end_date;
    // }

}